import { Box, Button, CircularProgress, Menu, MenuItem, SvgIcon, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { PATH_EDIT, STATE_DRAFT, STATE_PUBLISHED } from '@opengov/stories-shared/constants';
import { IPublication, IStory, IStoryContent, ProgressTrackerSettingType } from '@opengov/stories-shared/types';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';

import { storyPreviewPath } from '../../../../routes/paths';
import {
  IExtendedStoryPrivilege,
  IModalMessages,
  IPreviewPdfProps,
  ISettings,
  IStoryViewMessages,
  IUpdateStoryProps
} from '../../../../types';
import {
  STORIES_GRANULAR_ACCESS
} from '../../../constants';
import { getNewestUpdatedAt } from '../../../utils/helper';
import { Header } from '../../StoriesPage';
import { FileEditOutline } from '@opengov/react-capital-assets';
import { styles } from './Header.styles';

export type IHeaderProps = {
  isTemplate: boolean;
  isAdmin: boolean;
  isPublishing: boolean;
  isSaving: boolean;
  hasGallery: boolean;
  canCopyIndividualStories: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport?: boolean;
  lastPath: string;
  storyId: string;
  userId: number;
  entityId: number;
  entitySubdomain: string;
  story: IStory;
  publication?: IPublication;
  settings: ISettings;
  storyContents: IStoryContent[];
  messages: IStoryViewMessages;
  modalMessages: IModalMessages;
  displayPublicationTab: boolean;
  isStorySetupPdfEnabled?: boolean;
  storiesAccessControlEnabled: boolean;
  currentSubjectPublicationStoryClaim: IExtendedStoryPrivilege | null;
  updateStory(props: IUpdateStoryProps): Promise<void>;
  publishStory(props: {
    isTemplate: boolean;
    isAdmin: boolean;
    hasGallery: boolean;
    entitySubdomain: string;
    userId: number;
    story: IStory;
    settings: ISettings;
    storyContents: IStoryContent[];
    progressTrackerSettings?: ProgressTrackerSettingType[];
  }): Promise<void>;
  previewPdf(props: IPreviewPdfProps): Promise<void>;
  refreshEmbeds(props: {
    isTemplate: boolean;
    isAdmin: boolean;
    hasGallery: boolean;
    entitySubdomain: string;
    settings: ISettings;
    story: IStory;
    storyContents: IStoryContent[];
    messages: IModalMessages;
  }): Promise<void>;
  onDeleteClick(): void;
  onCopyStoryClick(): void;
  onCopyToTemplateClick(): void;
  onStorySettingsClick(): void;
  onApplyBrandingClick(): void;
  onExportClick(): void;
  openModal(modal: string): void;
  navigateToStoryList(): void;
  navigateToStoryEdit(): void;
  navigateToPublicationEdit(storyId: string, isTemplate: boolean, publicationId: string): void;
  navigateToStoryView(): void;
  navigateToPublicationView(): void;
  navigateToPublicationList(): void;
  updatePublicationUpdatedAt?(props: { publicationId: string; userId: number }): Promise<void>;
  updateThumbnail(publicationId: string, controller?: AbortController): void;
  navigateToStorySetupPDF(storyId: string): void;
  navigateToPublicationStorySetupPDF(storyId: string, publicationId: string): void;
};

export const StoryViewHeader: React.FC<IHeaderProps> = (props) => {
  const {
    canCopyIndividualStories,
    canDelete,
    canEdit,
    canExport,
    displayPublicationTab,
    entitySubdomain,
    hasGallery,
    isAdmin,
    isPublishing,
    isSaving,
    isTemplate,
    lastPath,
    messages,
    modalMessages,
    navigateToPublicationEdit,
    navigateToPublicationList,
    navigateToPublicationStorySetupPDF,
    navigateToPublicationView,
    navigateToStoryEdit,
    navigateToStoryList,
    navigateToStorySetupPDF,
    onApplyBrandingClick,
    onCopyStoryClick,
    onCopyToTemplateClick,
    onDeleteClick,
    onExportClick,
    onStorySettingsClick,
    openModal,
    publication,
    refreshEmbeds,
    settings,
    story,
    storyContents,
    storiesAccessControlEnabled,
    updatePublicationUpdatedAt,
    updateStory,
    updateThumbnail,
    userId
  } = props;

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  moment.updateLocale('en', {
    relativeTime: {
      future: messages.justNow,
      past: '%s ago',
      s: 'a few seconds',
      ss: '%d seconds',
      m: 'a minute',
      mm: '%d minutes',
      h: 'an hour',
      hh: '%d hours',
      d: 'a day',
      dd: '%d days',
      M: 'a month',
      MM: '%d months',
      y: 'a year',
      yy: '%d years'
    }
  });

  const closeMenu = () => setMenuAnchorEl(null);

  const handleRefresh = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    closeMenu();
    void refreshEmbeds({
      isTemplate,
      isAdmin,
      hasGallery,
      entitySubdomain,
      settings,
      story,
      storyContents,
      messages: modalMessages
    });
  };

  const handleOpenDialog = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    closeMenu();
    openModal(STORIES_GRANULAR_ACCESS);
  };

  const handleSaveAsTemplate = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    closeMenu();
    onCopyToTemplateClick();
  };

  const handleCopyStory = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    closeMenu();
    onCopyStoryClick();
  };

  const handleExportPDF = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onExportClick();
  };

  const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    closeMenu();
    onDeleteClick();
  };

  const handleApplyBranding = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onApplyBrandingClick();
    closeMenu();
  };

  const handleSetupPdf = () => {
    if (publication?.id) {
      navigateToPublicationStorySetupPDF(story._id, publication.id);
    } else {
      navigateToStorySetupPDF(story._id);
    }
  };

  const handleUpdate = () => {
    void updateStory({
      isTemplate,
      isAdmin,
      hasGallery,
      shouldRedirect: true,
      entitySubdomain,
      settings,
      story: { ...story },
      storyContents,
      publicationId: publication?.id || '',
      userId,
      currentSubjectPublicationStoryClaim: props.currentSubjectPublicationStoryClaim,
      storiesAccessControlEnabled
    });
    void updatePublicationUpdatedAt?.({ publicationId: publication?.id, userId });
    publication?.id && updateThumbnail(publication.id);
  };

  const handleNavigation = (event: React.MouseEvent<HTMLElement>, hasNavigatedFromPublication: boolean) => {
    event.preventDefault();
    hasNavigatedFromPublication
      ? navigateToPublicationEdit(story._id, isTemplate, publication?.id)
      : navigateToStoryEdit();
  };

  const renderStatus = () => {
    const { updatedAt } = story;
    const sortedDateTimes = getNewestUpdatedAt(storyContents, updatedAt);
    let status: string;
    if (isSaving) {
      status = `${messages.saving}...`;
    } else if (moment.utc(sortedDateTimes[0]).fromNow() === messages.aFewSecondsAgo) {
      status = messages.allChangesSaved;
    } else {
      status = `${messages.saved} ${moment.utc(sortedDateTimes[0]).fromNow()}`;
    }

    return (
      <Typography component="span" sx={styles.statusText} data-test="story-saved-status">
        {status}
      </Typography>
    );
  };

  const renderExportButton = () => (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      data-test="story-export-pdf"
      onClick={handleExportPDF}
      disabled={isSaving || isPublishing}
    >
      {messages.pdfExport}
    </Button>
  );

  const renderSetupPdfButton = () => (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      data-test="story-setup-pdf"
      onClick={handleSetupPdf}
      disabled={isSaving || isPublishing}
      startIcon={<SvgIcon component={FileEditOutline} inheritViewBox fontSize="small" aria-hidden={true} />}
    >
      {messages.setupPdf}
    </Button>
  );

  const renderMenuButton = (deleteText: string) => {
    if (!canEdit) return null;

    const showCopyStoriesOption = canCopyIndividualStories && !isTemplate && lastPath !== PATH_EDIT;
    const showApplyBranding = !isTemplate && lastPath === PATH_EDIT;

    return (
      <>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          data-test="story-menu-dropdown"
          disabled={isSaving || isPublishing}
          endIcon={<ArrowDropDownIcon aria-hidden={true} />}
          onClick={(e) => setMenuAnchorEl(e.currentTarget)}
        >
          {messages.menu}
        </Button>
        <Menu
          id="story-menu-dropdown"
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {showApplyBranding && (
            <MenuItem data-test="export-branding" onClick={handleApplyBranding}>
              {messages.applyBranding}
            </MenuItem>
          )}
          {!isEmpty(story.embeds) && lastPath === PATH_EDIT && (
            <MenuItem onClick={handleRefresh}>{messages.refreshEmbeds}</MenuItem>
          )}
          {isAdmin && (
            <MenuItem data-test="story-save-as-template" onClick={handleSaveAsTemplate}>
              {messages.saveAsTemplate}
            </MenuItem>
          )}
          {showCopyStoriesOption && isAdmin && (
            <MenuItem data-test="copy-story" onClick={handleCopyStory}>
              {messages.copyStory}
            </MenuItem>
          )}
          {storiesAccessControlEnabled && isAdmin && publication?.id && (
            <MenuItem data-test="manage-access" onClick={handleOpenDialog}>
              {messages.manageAccess}
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem data-test="delete-story" onClick={handleDelete}>
              {deleteText}
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const renderActionButton = () => {
    if (!canEdit) return null;
    const { _id, state, public_url } = story;
    const pattern = /^((http|https):\/\/)/;
    let publicUrl = public_url;
    if (!pattern.test(publicUrl)) {
      publicUrl = `http://${publicUrl}`;
    }

    if (lastPath === PATH_EDIT) {
      if (isSaving || isPublishing) {
        return (
          <Button variant="outlined" color="secondary" size="small" disabled data-test="story-preview" >
            {messages.preview}
          </Button>
        );
      }
      return (
        <Button
          component="a"
          href={storyPreviewPath(entitySubdomain, _id, isTemplate)}
          rel="noreferrer noopener"
          target="_blank"
          variant="outlined"
          color="secondary"
          size="small"
          data-test="story-preview"
        >
          {messages.preview}
        </Button>
      );
    } else if (state === STATE_PUBLISHED) {
      return (
        <Button
          component="a"
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          color="secondary"
          size="small"
        >
          {messages.view}
        </Button>
      );
    }
    return null;
  };

  const renderPrimaryButton = (pageText: string, updateText: string, isLoading: boolean, publicationId?: string) => {
    if (!canEdit) return null;

    if (lastPath === PATH_EDIT) {
      return (
        <Button
          variant="contained"
          size="small"
          onClick={handleUpdate}
          disabled={isSaving || isPublishing}
          data-test="story-save-and-exit"
          startIcon={isLoading ? <CircularProgress size={14} color="inherit" aria-label="Saving" /> : undefined}
        >
          {updateText}
        </Button>
      );
    } else if (isTemplate) {
      return (
        <Button
          variant="contained"
          size="small"
          disabled={isSaving || isPublishing}
          data-test="story-template-update"
          onClick={(event) => handleNavigation(event, !!publicationId)}
        >
          {pageText}
        </Button>
      );
    } else {
      return (
        <Button
          variant="contained"
          size="small"
          disabled={isSaving || isPublishing}
          data-test="story-edit"
          onClick={(event) => handleNavigation(event, !!publicationId)}
        >
          {pageText}
        </Button>
      );
    }
  };

  const { title, state } = story;
  const breadcrumbs = [title];
  const pageText = messages.edit;
  let updateText: string;
  let deleteText: string;
  let isLoading: boolean;

  if (isTemplate) {
    updateText = isSaving ? messages.saving : messages.saveAndExit;
    deleteText = `${messages.deleteText} ${messages.template}`;
    isLoading = isSaving;
  } else if (state === STATE_DRAFT) {
    updateText = isPublishing ? messages.publishing : messages.saveAndExit;
    deleteText = `${messages.deleteText} ${messages.draft}`;
    isLoading = isPublishing;
  } else {
    updateText = messages.saveAndExit;
    deleteText = messages.deleteText;
    isLoading = isSaving;
  }

  return (
    <Header
      breadcrumbs={breadcrumbs}
      publication={publication}
      navigateToStoryList={navigateToStoryList}
      navigateToPublicationView={navigateToPublicationView}
      navigateToPublicationList={navigateToPublicationList}
      displayPublicationTab={displayPublicationTab}
      isAdmin={isAdmin}
      canExport={canExport}
      canEdit={canEdit}
      storiesAccessControlEnabled={storiesAccessControlEnabled}
      onEditClick={onStorySettingsClick}
    >
      <Box sx={styles.actionButtons}>
        {renderStatus()}
        {canExport && props.isStorySetupPdfEnabled && renderSetupPdfButton()}
        {canExport && !props.isStorySetupPdfEnabled && renderExportButton()}
        {renderMenuButton(deleteText)}
        {renderActionButton()}
        {renderPrimaryButton(pageText, updateText, isLoading, publication?.id)}
      </Box>
    </Header>
  );
};
